import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import {
  getOrganizationSchema,
  getWebSiteSchema,
  getServiceSchema,
  getFAQSchema,
} from './jsonLdSchemas';

const BASE_URL = 'https://tropiphone.com';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;
const SITE_NAME = 'TropiPhone';

const SCHEMA_MAP = {
  Organization: getOrganizationSchema,
  WebSite: getWebSiteSchema,
  Service: getServiceSchema,
  FAQPage: getFAQSchema,
};

export default function SEOHead({
  title,
  description,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  noindex = false,
  schemas = [],
}) {
  const fullTitle = `${title} | ${SITE_NAME}`;
  const canonicalUrl = `${BASE_URL}${path}`;

  return (
    <Helmet>
      {/* Básicos */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <link rel="canonical" href={canonicalUrl} />
      <meta http-equiv="content-language" content="es" />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {schemas.map((schemaName) => {
        const schemaFn = SCHEMA_MAP[schemaName];
        if (!schemaFn) return null;
        return (
          <script key={schemaName} type="application/ld+json">
            {JSON.stringify(schemaFn())}
          </script>
        );
      })}
    </Helmet>
  );
}

SEOHead.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  path: PropTypes.string,
  image: PropTypes.string,
  noindex: PropTypes.bool,
  schemas: PropTypes.arrayOf(PropTypes.oneOf(['Organization', 'WebSite', 'Service', 'FAQPage'])),
};
